// @flow
import * as React from "react";
import styled, { css } from "styled-components";
import Gettext from "../../common/Gettext";
import { Link } from "../../common/Link";
import { withFetch } from "../../common/fetch";
import NameImage from "../../../images/logged-in-name.png";
import VipImage from "../../../images/vip-icon.png";
import TradeImage from "../../../images/trade-button.png";
import _Money from "../../Money";
import { fontSize, rtl } from "../../common/styled-mixins";
import type { Profile } from "../../../redux/CommonTypes";
import type { Dispatch } from "../../../redux/action";

import {
  TournamentsIcon,
  TradingHistoryIcon,
  PersonIcon,
  QuitIcon
} from "../../Navigation/Icons";
import { gaReducer } from "../../GA";

const Name = styled.h1`
  direction: ${props => props.theme.direction};
  margin-left: -40px;
  padding-left: 40px;
  font-size: 24px;
  line-height: 1.2;
  color: #fff;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  background: url(${NameImage}) 0% 3px no-repeat;

  ${props =>
    props.theme.direction === "rtl" &&
    css`
      margin-left: 0;
      padding-left: 0;
      padding-right: 40px;
      text-align: right;
      background-position-x: right;
    `};

  @media (max-width: 899px) {
    margin-left: 0;
  }

  @media (max-width: 479px) {
    font-size: 18px;
    line-height: 24px;
  }
`;

const LineContainer = styled.div`
  padding: 12px 0;
`;

const IdContainer = styled.div`
  direction: ${props => props.theme.direction};
  ${rtl("text-align", "left")};
`;

const Id = styled.span`
  direction: ${props => props.theme.direction};
  display: inline-block;
  font-size: 16px;
  line-height: 1;
  vertical-align: middle;
  ${rtl("margin-right", "10px")};
  color: #fff;
  opacity: 0.6;

  :before {
    content: "ID: ";
  }
`;

const Vip = styled.span`
  display: inline-block;
  vertical-align: middle;
  font-size: 0;
  line-height: 0;
  width: 45px;
  height: 17px;
  background: url(${VipImage}) no-repeat 0 0;
  background-size: cover;
`;

const Silver = styled.span`
  display: inline-block;
  position: relative;
  top: -2px;
  padding: 2px 5px 2px;
  border: 1px solid #878993;
  border-radius: 3px;
  font-size: ${fontSize(10)};
  line-height: 1;
  text-transform: uppercase;

  ${props =>
    props.thProject &&
    css`
      display: none;
    `};
`;

const Account = styled.div`
  direction: ${props => props.theme.direction};
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 32px;
  ${rtl("text-align", "left")};
`;

const AccountContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  max-width: 240px;
  white-space: nowrap;

  @media (max-width: 359px) {
    max-width: 200px;
  }
`;

const AccountHeader = styled.span`
  direction: ${props => props.theme.direction};
  ${rtl("margin-right", "5px")};
  font-size: ${fontSize(16)};
  line-height: 1;
  color: #fff;
  opacity: 0.6;

  @media (min-width: 360px) {
    padding-top: 2px;
  }
`;

const Button = styled(Link)`
  display: inline-block;
  vertical-align: middle;
  padding: 0 28px;
  margin: 0;
  border: 0;
  border-radius: 8px;
  font-weight: 700;
  font-size: 22px;
  line-height: 48px;
  text-align: center;
  text-decoration: none;
  color: #fff;
  user-select: none;
  cursor: pointer;

  @media (max-width: 359px) {
    padding: 0 15px;
  }

  :focus {
    outline: none;
  }
`;

const FillupButton = styled(Button)`
  height: 32px;
  width: 90px;
  display: block;
  font-size: ${fontSize(14)};
  line-height: 32px;
  text-align: center;
  padding: 0;
  border-radius: 4px;
  background-color: #60a31c;
  background: linear-gradient(to bottom, #68c703 0%, #60a31c 100%);

  :hover {
    background-color: #68c703;
    background: linear-gradient(to bottom, #60a31c 0%, #68c703 100%);
  }
`;

const FillupButtonIron = styled(Button)`
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  height: 32px;
  width: 100px;
  padding: 5px;
  font-size: ${fontSize(14)};
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
  color: #ffffff;
  background: transparent;
  border: 2px solid #ffffff;
  border-radius: 24px;

  :hover,
  :focus {
    background-image: none;
    background-color: #ffffff;
    color: #3b9478;
  }
`;

const TradeButton = styled(Button)`
  position: relative;
  display: block;
  padding: 20px 10px 20px 73px;
  margin: 0;
  text-align: left;
  font: 700 28px/1 Roboto, Arial, sans-serif;
  text-shadow: 0 -1px 1px rgba(0, 0, 0, 0.22);
  box-shadow: inset 0 -2px 0 0 rgba(0, 0, 0, 0.3),
    inset 0 -2px 0 0 rgba(255, 255, 255, 0.1);
  background-color: #feac00;
  background: linear-gradient(to bottom, #feac00 0%, #fd9500 100%);

  @media (max-width: 479px) {
    font-size: 22px;
    line-height: 48px;
    width: 100%;
    text-align: center;
    padding: 10px;
    padding-left: 57px;
  }

  ${props =>
    props.smallText &&
    css`
      font-size: 21px;

      @media (max-width: 479px) {
        font-size: 18px;
      }
    `};

  :before {
    content: "";
    position: absolute;
    top: 50%;
    left: 22px;
    transform: translateY(-50%);
    width: 34px;
    height: 34px;
    background: url(${TradeImage}) center no-repeat;
  }

  :hover {
    background-color: #fd9500;
    background: linear-gradient(to bottom, #fd9500 0%, #feac00 100%);
  }
`;

const TradeButtonIron = styled(Link)`
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  padding: 8px 20px;
  width: 100%;
  font-size: ${({ theme }) => (theme.locale === "vn" ? "22px" : "24px")};
  text-transform: uppercase;
  text-decoration: none;
  color: ${({ theme }) => theme.main};
  background-color: ${({ theme }) => theme.mainText};
  border-radius: 32px;
  cursor: pointer;
  transition: all 0.3s;

  :hover {
    background-color: transparent;
    color: ${({ theme }) => theme.mainText};
    box-shadow: inset 0px 0px 0px 2px;
  }

  @media (max-width: 959px) {
    order: 0;
    font-size: 18px;
  }
`;

const Money = styled(_Money)`
  font-size: ${fontSize(18)};
  line-height: 1;

  @media (max-width: 359px) {
    font-size: ${fontSize(16)};
  }
`;

const Sum = styled(_Money.Value)`
  font-weight: 700;
  color: #fff;
`;

const Currency = styled(_Money.Symbol)`
  font-weight: 700;
  color: #fff;
`;

const LinksWrapper = styled.div`
  direction: ${({ theme }) => theme.direction};
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: 2px 0;
`;

const IconContainer = styled.i`
  position: relative;
  display: flex;
  align-items: center;
  width: 19px;
  ${rtl("margin-right", "10px")};
  backface-visibility: hidden;
  color: currentColor;
  opacity: 0.6;
`;

const LinkContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  width: fit-content;
  padding: 3px 0;
  color: #fff;

  :hover ${IconContainer} {
    opacity: 1;
  }
`;

const ActualLink = styled(Link)`
  font-size: ${fontSize(14)};
  line-height: 26px;
  text-decoration: none;
  color: ${({ theme }) => theme.accent};

  :hover {
    text-decoration: underline;
  }
`;

type LinkProps = {
  to: string,
  replace?: boolean,
  Icon: React.ComponentType<*>,
  children: React.Node
};

function LinkWithIcon({ to, replace, children, Icon }: LinkProps) {
  return (
    <LinkContainer>
      <IconContainer>
        <Icon width="19px" />
      </IconContainer>
      <ActualLink replace={replace} to={to}>
        {children}
      </ActualLink>
    </LinkContainer>
  );
}

type Props = {
  fetch: Function,
  dispatch: Dispatch,
  profile: Profile,
  geo: string,
  isVIP: boolean
};

class LoggedIn extends React.Component<Props> {
  render() {
    const { profile, geo, isVIP } = this.props;
    const { email, first_name = "", last_name = "" } = profile;

    let displayName = "";
    if (geo === "VN") {
      displayName = profile.first_name;
    } else if (first_name || last_name) {
      displayName = first_name + "\u00A0" + last_name;
    }

    if (displayName === "") {
      displayName = email;
    }

    return (
      <div>
        <Name>{displayName}</Name>
        <LineContainer>
          <IdContainer>
            <Id>{profile.id}</Id>
            {isVIP ? (
              <Vip>VIP</Vip>
            ) : (
              <Silver
                thProject={process.env.REACT_APP_DEPLOY_TARGET === "siamoption"}
              >
                SILVER
              </Silver>
            )}
          </IdContainer>
          <Account>
            <AccountContainer>
              <AccountHeader>
                <Gettext>Balance</Gettext>:
              </AccountHeader>
              <Money Value={Sum} Symbol={Currency}>
                {profile.real ? profile.balance.real : profile.balance.demo}
              </Money>
            </AccountContainer>
            <Button
              as={
                process.env.REACT_APP_DEPLOY_TARGET === "irontrade"
                  ? FillupButtonIron
                  : FillupButton
              }
              to="/fillup"
              onClick={() => {
                gaReducer("CLICKBTN_DEPOSIT_MAINPAGE");
              }}
            >
              <Gettext>Deposit</Gettext>
            </Button>
          </Account>
          <LinksWrapper>
            <LinkWithIcon to="/profile" Icon={PersonIcon}>
              <Gettext>Profile</Gettext>
            </LinkWithIcon>
            <LinkWithIcon to="/tournaments" Icon={TournamentsIcon}>
              <Gettext>Tournaments</Gettext>
            </LinkWithIcon>
            <LinkWithIcon to="/trading-history" Icon={TradingHistoryIcon}>
              <Gettext>Trading history</Gettext>
            </LinkWithIcon>
            <LinkWithIcon replace to="/logout" Icon={QuitIcon}>
              <Gettext>Log out</Gettext>
            </LinkWithIcon>
          </LinksWrapper>
          <LineContainer>
            <Button
              as={
                process.env.REACT_APP_DEPLOY_TARGET === "irontrade"
                  ? TradeButtonIron
                  : TradeButton
              }
              to="/trade"
              smallText={profile.locale === "vn" || profile.locale === "ph"}
              onClick={() => {
                gaReducer("CLICKBTN_TRADE_MAINPAGE_HEADER");
              }}
            >
              <Gettext>Trade now</Gettext>
            </Button>
          </LineContainer>
        </LineContainer>
      </div>
    );
  }
}

export default withFetch(LoggedIn);



// WEBPACK FOOTER //
// ./src/components/Auth/Tabs/LoggedIn.js